<template>
  <div
    class="fixed bottom-5 transition-all duration-300"
    :style="{ right: slideInWidth ? `${slideInWidth + 20}px` : '20px' }"
  >
    <div
      v-for="(loader, index) in setSocketLoaders"
      :id="loader.id"
      :key="loader.id"
      class="shadow-on-top socket-loader mx-auto w-[232px] rounded-xl transition-all duration-300"
      :class="{
        'border-accent-bdr-green green-gradient': loader?.state === 'success',
        'border-accent-bdr-red red-gradient': loader?.state === 'error',
        'border-bdr-alpha bg-bgr-primary': loader?.state === 'loading',
      }"
    >
      <div
        class="flex items-center justify-center gap-2 rounded-lg px-3 py-1 transition-all duration-300"
        :class="{
          'bg-bgr-success-secondary': loader?.state === 'success',
          'bg-bgr-critical-secondary': loader?.state === 'error',
          'bg-bgr-tertiary': loader?.state === 'loading',
        }"
      >
        <Icon src="scan" class="" :size="ComponentSize.lg" />
        <p class="text-body-large-m pb-0.5">{{ loader?.title }}</p>
      </div>
      <div class="flex items-center justify-center gap-1 px-3 py-1.5">
        <p v-if="loader?.state === 'error'" class="text-body-default-m text-fgr-critical">
          {{ loader.errorText }}
        </p>
        <p v-if="loader?.state === 'success'" class="text-body-default-m text-fgr-success">
          {{ loader.successText }}
        </p>
        <Icon v-if="loader?.state === 'success'" class="text-fgr-success" src="checkCircle" :size="ComponentSize.lg" />
        <Icon v-if="loader?.state === 'error'" class="text-fgr-critical" src="warningCircle" :size="ComponentSize.lg" />
        <ShimmerLoader v-if="loader?.state === 'loading'" :loadingText="loader?.loadingText" />
      </div>
      <div v-if="loader.state === 'error'" class="bg-bgr-primary mt-1.5 rounded-lg p-2">
        <p class="text-fgr-critical text-body-default">{{ loader?.errorMessage }}</p>
      </div>
      <Button
        v-if="loader?.state === 'error' || loader?.state === 'success'"
        class="!text-fgr-critical mt-2 !h-7 w-full"
        :class="{
          '!text-fgr-critical': loader?.state === 'error',
          '!text-fgr-success': loader?.state === 'success',
        }"
        :variant="ButtonVariant.Ghost"
        size="md"
        @click="removeSocketLoader(loader?.id)"
      >
        {{ t("close") }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ISocketLoader } from "~/types/socketLoader";

  const { t } = useI18n();
  const { socketLoaders } = useSocket();
  const setSocketLoaders = ref(<ISocketLoader[]>([] as unknown));
  const slideInExists = ref(false);
  const slideInWidth = ref(0);

  watch(
    () => socketLoaders.value,
    (newVal) => {
      newVal.forEach((loader) => {
        if (!setSocketLoaders.value.find((l: ISocketLoader) => l.id === loader.id)) {
          setSocketLoaders.value.push(loader);
        }
      });
    },
    { deep: true }
  );

  // Watch for slideIn element existence
  onMounted(() => {
    const observer = new MutationObserver(() => {
      const slideIn = document.getElementById("slideInHeader");
      slideInExists.value = !!slideIn;
      slideInWidth.value = slideIn ? slideIn.offsetWidth : 0;
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial check
    const slideIn = document.getElementById("slideInHeader");
    slideInExists.value = !!slideIn;
    slideInWidth.value = slideIn ? slideIn.offsetWidth : 0;

    onUnmounted(() => {
      observer.disconnect();
    });
  });

  const shouldMove = computed(() => {
    return slideInExists.value;
  });

  const removeSocketLoader = (id: string) => {
    const loader = document.getElementById(id);
    loader?.classList.add("is-deleting");
    setTimeout(() => {
      socketLoaders.value = socketLoaders.value.filter((loader) => loader.id !== id);
      setSocketLoaders.value = setSocketLoaders.value.filter((loader) => loader.id !== id);
    }, 400);
  };
</script>

<style>
  .green-gradient {
    background: linear-gradient(180deg, rgba(255, 255, 255, 1), rgba(230, 249, 234, 1));
  }

  .red-gradient {
    background: linear-gradient(180deg, rgba(255, 255, 255, 1), #fef5f3);
  }
</style>
